/** DOM helpers for the standalone bar's next-prompt ghost. */

/** Least room that shows the after-chip ghost: the `tab` keycap (about 32px
 *  with its margin) plus a few characters of the prediction. With less, the
 *  ghost stays hidden and takes no Tab, no aria-description and no width. */
export const CHIP_GHOST_MIN_ROOM = 64;

/** Width a truncated element hides past its visible box. */
function hiddenWidth(element: HTMLElement | null): number {
  return element ? Math.max(0, element.scrollWidth - element.clientWidth) : 0;
}

/** Measure the suggested text as it wraps within the field. */
function ghostNaturalWidth(ghost: HTMLElement): number {
  return ghost.scrollWidth +
    hiddenWidth(ghost.querySelector<HTMLElement>("[data-standalone-ghost-text]"));
}

/** Right edge the ghost needs untruncated, measured from the field's left edge. */
export function ghostNaturalRight(ghost: HTMLElement): number {
  return ghost.offsetLeft + ghostNaturalWidth(ghost);
}

/** Whether `room` px right of the chip shows the after-chip ghost: all of it,
 *  or at least CHIP_GHOST_MIN_ROOM of it. */
export function chipGhostFits(ghost: HTMLElement, room: number): boolean {
  return room >= Math.min(CHIP_GHOST_MIN_ROOM, ghostNaturalWidth(ghost));
}

/** Start the after-chip ghost where typed text would: right of the chip (plus
 *  its margin), on the chip's line. A truncated chip label counts at its full
 *  width, so the ghost never covers it while the bar grows to fit both.
 *  `max-width` follows the field's width. Returns the room the field's current
 *  width leaves the ghost, or null without a chip. */
export function placeChipGhost(root: HTMLElement, ghost: HTMLElement, wrapper: HTMLElement): number | null {
  const chip = root.querySelector<HTMLElement>("[data-standalone-chip]");
  if (!chip) return null;
  const chipRect = chip.getBoundingClientRect();
  const wrapperRect = wrapper.getBoundingClientRect();
  const chipRight = chipRect.right + hiddenWidth(chip.querySelector<HTMLElement>("[data-standalone-chip-label]"));
  const gap = Number.parseFloat(getComputedStyle(chip).marginRight) || 0;
  const left = Math.max(0, chipRight + gap - wrapperRect.left);
  ghost.style.left = `${left}px`;
  ghost.style.top = `${chipRect.top - wrapperRect.top}px`;
  ghost.style.maxWidth = `calc(100% - ${left}px)`;
  const lineHeight = Number.parseFloat(getComputedStyle(ghost).lineHeight) || 20;
  if (wrapperRect.width > 0 && (wrapperRect.width - left < CHIP_GHOST_MIN_ROOM ||
    ghost.getBoundingClientRect().height > lineHeight + 1)) {
    ghost.style.left = "0px";
    ghost.style.top = `${chipRect.bottom - wrapperRect.top + 4}px`;
    ghost.style.maxWidth = "100%";
    return wrapperRect.width;
  }
  return wrapperRect.width - left;
}
